import React, { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { FUENTE, useTema } from './tema';

/**
 * Cuenta — quién ha iniciado sesión, y cómo salir.
 *
 * Hasta ahora la v2 no ofrecía ninguna forma de cerrar sesión: había que
 * borrar el almacenamiento del navegador. En un equipo compartido eso no es un
 * detalle, porque la sesión da acceso al calendario y a las grabaciones.
 *
 * Se cierra con ventana emergente para no perder la página; si el navegador la
 * bloquea, se cae a la redirección completa, que siempre funciona.
 */

const iniciales = (nombre, correo) => {
  const base = (nombre || correo || '?').trim();
  const partes = base.split(/[\s@._-]+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const estilos = (C) => ({
  caja: { display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 },
  avatar: {
    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 600, color: '#fff',
    background: 'linear-gradient(140deg, #6366f1 0%, #4f46e5 100%)'
  },
  datos: { minWidth: 0, flex: 1 },
  nombre: {
    fontSize: 13.5, fontWeight: 600, lineHeight: 1.3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  correo: {
    fontSize: 12, color: C.tenue, lineHeight: 1.3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  boton: {
    padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontFamily: FUENTE, whiteSpace: 'nowrap',
    background: 'transparent', color: C.suave,
    border: `1px solid ${C.borde}`
  },
  error: {
    fontSize: 12.5, color: C.error, marginTop: 9, lineHeight: 1.5
  }
});

/**
 * @param {Object} props
 * @param {boolean} props.enLinea - en una sola fila (para una barra lateral)
 *   o apilado con el botón debajo
 */
const Cuenta = ({ enLinea = false }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const { instance, accounts } = useMsal();
  const cuenta = accounts && accounts[0];

  const [saliendo, setSaliendo] = useState(false);
  const [error, setError] = useState(null);

  if (!cuenta) return null;

  const nombre = cuenta.name || cuenta.username || 'Tu cuenta';
  const correo = cuenta.username || '';

  const cerrarSesion = async () => {
    setSaliendo(true);
    setError(null);

    const peticion = {
      account: cuenta,
      // Vuelve a la pantalla de entrada de la app, no a la de Microsoft.
      postLogoutRedirectUri: window.location.origin
    };

    try {
      await instance.logoutPopup(peticion);
    } catch (e) {
      // La emergente puede estar bloqueada o haberse cerrado a medias. La
      // redirección completa no depende de eso.
      try {
        await instance.logoutRedirect(peticion);
      } catch (e2) {
        setError(`No se pudo cerrar la sesión: ${e2.errorMessage || e2.message}`);
        setSaliendo(false);
      }
    }
  };

  const boton = (
    <button
      style={S.boton}
      onClick={cerrarSesion}
      disabled={saliendo}
      title={`Cerrar la sesión de ${correo}`}
    >
      {saliendo ? 'Saliendo…' : 'Cerrar sesión'}
    </button>
  );

  return (
    <div>
      <div style={S.caja}>
        <span style={S.avatar}>{iniciales(cuenta.name, correo)}</span>
        <span style={S.datos}>
          <div style={S.nombre}>{nombre}</div>
          {correo && <div style={S.correo}>{correo}</div>}
        </span>
        {enLinea && boton}
      </div>

      {!enLinea && <div style={{ marginTop: 14 }}>{boton}</div>}
      {error && <div style={S.error} role="alert">{error}</div>}
    </div>
  );
};

export default Cuenta;
